/**
 * Product tour registry.
 *
 * A global notification (DEV-7438) can carry a `tour_key`. That key is the one
 * and only thing the backend stores — everything else about a tour lives
 * here: where it runs, what it points at, and what it says.
 *
 * Keeping the destination on the frontend is deliberate. `unplugin-vue-router`
 * generates a typed route map, so a `RouteLocationRaw` in this file is checked
 * against the routes that actually exist — rename a page and the build breaks.
 * A route stored as a backend string is an untyped pointer into another
 * codebase's routing table: rename `settings.vue` and every stored row quietly
 * 404s with nothing in CI to catch it.
 *
 * A "highlight this feature" callout is not a separate concept — it is just a
 * tour with a single step (driver.js has a first-class `highlight()` for it,
 * which `useProductTour` picks automatically).
 */
import type { Alignment, Side } from 'driver.js'
import type { RouteLocationRaw } from 'vue-router'
import { clickAnchorIfPresent } from '~/utils/tourAnchors'

/**
 * Identifies a product tour.
 *
 * **This registry is the single source of truth for what tours exist.** The
 * backend column is free text (a plain nullable string) with a bare text input
 * in admin — it is deliberately not an enum, so there is no generated TS union
 * to consume and no compile-time coupling in either direction. A key that does
 * not appear here simply does not resolve at runtime.
 *
 * The cost of free text is that a typo in admin fails *silently*: the CTA
 * correctly hides itself, so an announcement ships looking tour-less and
 * nobody finds out until a customer complains. The unknown-key warning in
 * `useNotificationTourCta` is the only thing that surfaces such a mistake —
 * treat it as load-bearing, not as polish.
 */
export const PRODUCT_TOUR_KEYS = {
  AutoBookProfiles: 'auto_book_profiles',
  ExportsVesselSchedule: 'exports_vessel_schedule',
} as const
export type ProductTourKey =
  (typeof PRODUCT_TOUR_KEYS)[keyof typeof PRODUCT_TOUR_KEYS]

/**
 * Label on every CTA that launches a tour, whichever notification surface
 * offers it.
 *
 * A frontend constant rather than a backend column on purpose: the CTA label is
 * product copy, so rewording it should not need a migration — and it must read
 * identically on the popup and the banner, which it cannot if each admin who
 * publishes an announcement retypes it.
 */
export const PRODUCT_TOUR_CTA_LABEL = 'Take me there'

export interface ProductTourStep {
  /** Value of the `data-tour` attribute on the element to highlight. */
  anchor: string
  title: string
  description: string
  side?: Side
  align?: Alignment
  /**
   * Route to navigate to before this step runs.
   *
   * Preferred over clicking a nav item in `prepare`: nav entries wire up
   * navigation in ways a synthetic click does not necessarily trigger
   * (`NavMenuItem` puts an invisible `<a>` *inside* the menu item, and clicks
   * on the item never reach it), and going through the router keeps the
   * destination type-checked. Highlight the nav item, navigate for real.
   */
  navigateTo?: RouteLocationRaw
  /**
   * Runs before this step is shown, for steps whose target does not exist
   * yet — opening a dialog, expanding a panel. The step's own anchor is
   * awaited afterwards, so `prepare` only needs to trigger the change, not
   * wait for it.
   */
  prepare?: () => void | Promise<void>
  /**
   * Undoes this step's own `prepare`, so Previous can get back.
   *
   * Only needed when `prepare` did something that hides the *previous* step's
   * anchor — opening a modal over it, say. Stepping back runs the step you are
   * leaving through `revert`, which by definition restores the state the
   * previous step ran in; the previous step's `prepare` is deliberately not
   * replayed, since that state is exactly what it produced the first time.
   *
   * A `prepare` that only reveals things (switching a tab, expanding a panel)
   * needs no `revert` — the earlier anchors are all still on screen.
   */
  revert?: () => void | Promise<void>
}

export interface ProductTour {
  /**
   * Where the feature lives.
   *
   * Two jobs, which is why it is required rather than derived from the steps:
   *
   * - it is where the CTA falls back to when the tour cannot run (a renamed
   *   `data-tour`, a feature behind a flag the viewer lacks). Landing the user
   *   on the feature is most of the value even with no popovers.
   * - it is the page a `jumpToFeature` tour opens on.
   */
  featureRoute: RouteLocationRaw
  /**
   * Navigate straight to `featureRoute` before the first step, instead of
   * walking the user there.
   *
   * **The exception, not the default.** Teleporting someone to a page they did
   * not navigate to is precisely the disorientation a tour exists to prevent,
   * so a guided tour — first step anchored on something already on screen,
   * later steps carrying their own `navigateTo` — is what a tour should
   * normally be. Jumping earns its keep only for a feature buried deeper than
   * a couple of levels, where the walk turns tedious.
   */
  jumpToFeature?: boolean
  steps: ProductTourStep[]
}

/**
 * The dropdown only exists for users who can create empty-return profiles, so
 * it gets a short timeout — for everyone else the plain button already opened
 * the dialog and there is nothing to wait for.
 */
const DROPDOWN_ITEM_TIMEOUT_MS = 500

const EXPORTS_SCHEDULE_ROUTE: RouteLocationRaw = {
  path: '/exports',
  query: { view: 'schedule' },
}

export const PRODUCT_TOURS: Partial<Record<ProductTourKey, ProductTour>> = {
  // Walks the route the user would take themselves: Settings → Auto Book
  // Profiles tab → Add Profile → the dialog. They see where the feature lives
  // and can find it again unaided.
  [PRODUCT_TOUR_KEYS.AutoBookProfiles]: {
    featureRoute: { path: '/settings', query: { tab: 'auto_book' } },
    steps: [
      {
        anchor: 'nav-settings',
        title: 'Start in Settings',
        description:
          'Auto Book Profiles live in your Settings. You can always get ' +
          'there from this menu on the left.',
        side: 'right',
        align: 'center',
      },
      {
        // Deliberately without `?tab=auto_book` — the user should land on
        // Settings as they normally would and then be shown the tab
        navigateTo: { path: '/settings' },
        anchor: 'settings-tab-auto-book',
        title: 'Open the Auto Book Profiles tab',
        description:
          'Settings is split into tabs down the side. Auto Book Profiles is ' +
          'this one.',
        side: 'right',
        align: 'center',
      },
      {
        prepare: async () => {
          await clickAnchorIfPresent('settings-tab-auto-book')
        },
        anchor: 'auto-book-add-profile',
        title: 'Add a profile',
        description:
          'This is the page. A profile is a reusable set of Auto Book ' +
          'settings you can apply to containers in a single click.',
        side: 'bottom',
        align: 'end',
      },
      {
        // Users who can create empty-return profiles get a dropdown here
        // rather than a plain button, so the menu item is clicked too if one
        // appears. Either way the dialog is what this step's anchor waits for.
        prepare: async () => {
          await clickAnchorIfPresent('auto-book-add-profile')
          await clickAnchorIfPresent(
            'auto-book-add-profile-import',
            DROPDOWN_ITEM_TIMEOUT_MS
          )
        },
        // The dialog is modal, so without closing it again Previous would
        // point at an Add Profile button the user cannot see or reach. Cancel
        // rather than the ✕ because it is the same "close without saving" the
        // user would pick, and it is disabled mid-save — which is exactly when
        // we should not close.
        revert: async () => {
          await clickAnchorIfPresent('auto-book-profile-cancel')
        },
        anchor: 'auto-book-profile-name',
        title: 'Name the profile',
        description:
          'Give the profile a name you will recognize when applying it — ' +
          '"Morning LA" or "Flexible weekday", for example.',
        side: 'bottom',
        align: 'start',
      },
      {
        anchor: 'auto-book-profile-settings',
        title: 'Choose the booking preferences',
        description:
          'Day, time, hold and shift preferences. These are exactly the ' +
          'settings Auto Book uses when it books a container for you.',
        side: 'top',
        align: 'center',
      },
      {
        anchor: 'auto-book-profile-save',
        title: 'Save and reuse it',
        description:
          'Saved profiles show up in the Auto Book dialog. Editing one later ' +
          'only affects future applications, never containers already booked.',
        side: 'top',
        align: 'end',
      },
    ],
  },
  // Read-only end to end, which is what makes the whole board tourable: the
  // steps point at the filters and the board itself, and the one interactive
  // thing on the page — opening a row's change log — is described rather than
  // performed. Clicking a row would mean picking one of the user's vessels,
  // and there may not be one (a new org, or a board filtered to nothing).
  [PRODUCT_TOUR_KEYS.ExportsVesselSchedule]: {
    // Asked for explicitly: the bare path opens whichever Exports view the
    // user last had (List by default), and this tour only works on Schedule.
    featureRoute: EXPORTS_SCHEDULE_ROUTE,
    // Anchors run top-to-bottom down the page — nav, the filter row left to
    // right, then the board's header band and its rows — so the highlight only
    // ever moves forward and the tour ends on the board, which is the thing
    // being announced.
    //
    // The sort step is the one anchored inside the table, so it is also the
    // one that disappears on an empty board (the board swaps the table for
    // NoSearchResults). That degrades correctly — the step is skipped — but it
    // does cost a Sentry warning, so it sits late enough that the user has
    // already seen the filters that would explain an empty result.
    steps: [
      {
        anchor: 'nav-exports',
        title: 'Start in Exports',
        description:
          'The vessel schedule lives under Exports. It lists every vessel ' +
          'calling your terminals, with the dates you plan bookings around.',
        side: 'right',
        align: 'center',
      },
      {
        navigateTo: EXPORTS_SCHEDULE_ROUTE,
        anchor: 'exports-vessel-search',
        title: 'Find a vessel',
        description:
          'Search by vessel name or voyage number. Matching is on those two ' +
          'fields only.',
        side: 'bottom',
        align: 'start',
      },
      {
        anchor: 'exports-terminal-filter',
        title: 'Narrow it to your terminals',
        description:
          'Pick one or more terminals to see only their calls. A vessel ' +
          'calling two terminals has a row for each, so this is what ' +
          'separates them.',
        side: 'bottom',
        align: 'center',
      },
      {
        anchor: 'exports-schedule-status-filter',
        title: 'Filter by where a vessel is in its cycle',
        description:
          'Scheduled is not receiving yet, Open is taking cargo now, and ' +
          'Departed is past its cargo cutoff. Pick any combination — with ' +
          'none selected you see them all.',
        side: 'bottom',
        align: 'center',
      },
      {
        anchor: 'exports-schedule-sort',
        title: 'Sort by any date',
        description:
          'ETA, ERD and all three cutoffs sort. Click a header to sort by ' +
          'it, click again to reverse. The board opens on soonest ERD first, ' +
          'so what you can start dropping for is at the top.',
        side: 'bottom',
        align: 'start',
      },
      {
        anchor: 'exports-schedule-board',
        title: 'Read the dates that matter',
        description:
          'One row per vessel and terminal. ERD is when the terminal starts ' +
          'receiving; the cargo, reefer and hazmat cutoffs are when it stops. ' +
          'Click a row to see every change the terminal has made to those ' +
          'dates, with timestamps.',
        side: 'top',
        align: 'center',
      },
    ],
  },
}

/**
 * Where a tour's feature lives, for callers that need to get the user there
 * without running the tour. `undefined` for a key with no registry entry.
 */
export function productTourFeatureRoute(
  key: ProductTourKey
): RouteLocationRaw | undefined {
  return PRODUCT_TOURS[key]?.featureRoute
}
